import "~/css/style.css";

import moonSvg from "~/img/svg/moon.svg";
import sunSvg from "~/img/svg/sun.svg";
import { isMobile, isTablet } from "~/js/helpers";
import { Popover } from "~/js/utils/popover";

let coreDialogModulePromise;
const loadCoreDialog = () => {
	coreDialogModulePromise ??= import(/* webpackChunkName: "dialog-core" */ "~/js/utils/dialog.js");
	return coreDialogModulePromise;
};

async function initDialogMenu() {
	const openButton = document.querySelector("#openMenu");
	if (!openButton) return;
	const template = document.querySelector("#dialogMenu");
	if (!(template instanceof HTMLTemplateElement)) return;

	// Current page: the link with the longest matching path ("/" only on itself).
	const path = location.pathname;
	let current = null;
	for (const link of template.content.querySelectorAll('a[href^="/"]')) {
		const href = link.getAttribute("href");
		const matches = href === path || (href !== "/" && path.startsWith(href));
		if (matches && href.length > (current?.getAttribute("href").length ?? 0)) current = link;
	}
	current?.setAttribute("aria-current", "page");

	let dialog;
	let opening;
	openButton.addEventListener("click", async () => {
		if (opening) return;
		opening = (async () => {
			if (!dialog) {
				const { Dialog } = await loadCoreDialog();
				dialog = new Dialog({
					id: "navigationDialog",
					title: gettext("Navigazione"),
					variant: "drawer",
				});
			}
			await dialog.open(template);
		})();
		try {
			await opening;
		} catch {
			alert(gettext("Impossibile aprire la finestra. Ricarica la pagina e riprova."));
		} finally {
			opening = null;
		}
	});
}

async function initDialogUserReport() {
	const openButtons = document.querySelectorAll("[data-dialog='user-report']");
	if (!openButtons.length) return;

	let modulePromise;
	for (const openButton of openButtons) {
		const reportId = openButton.dataset.id || null;
		let controller;
		let opening;

		openButton.addEventListener("click", async () => {
			if (opening) return;
			opening = (async () => {
				modulePromise ??= import(
					/* webpackChunkName: "user-report-dialog" */ "~/js/dialogs/user-report.js"
				);
				const { createUserReportDialog } = await modulePromise;
				controller ??= createUserReportDialog({ reportId });
				const result = await controller.open();
				if (result.status === "submitted" && result.value?.action === "delete") {
					window.location.reload();
				}
			})();
			try {
				await opening;
			} catch {
				alert(gettext("Impossibile aprire la finestra. Ricarica la pagina e riprova."));
			} finally {
				opening = null;
			}
		});
	}
}

// Syncs the lime schedule indicator on report rows after the schedule dialog resolves.
function syncScheduleIndicators(data) {
	if (!data?.userReportId || typeof data.hasSchedules !== "boolean") return;

	if (!data.hasSchedules && new URLSearchParams(window.location.search).get("scheduled") === "1") {
		// The index can be filtered to scheduled reports only: removing the
		// last schedule drops the row, which needs a server render.
		window.location.reload();
		return;
	}

	const id = CSS.escape(data.userReportId);
	const hosts = document.querySelectorAll(`[data-schedule-dot-host][data-id='${id}']`);
	for (const host of hosts) {
		const indicator = host.querySelector("[data-schedule-indicator]");
		if (data.hasSchedules && !indicator) {
			const dot = document.createElement("span");
			dot.setAttribute("data-schedule-indicator", "");
			dot.className =
				"absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-foreground bg-lime-500";
			dot.setAttribute("aria-hidden", "true");
			const label = document.createElement("span");
			label.className = "sr-only";
			label.textContent = gettext("Programmazione presente");
			host.append(dot, label);
		} else if (!data.hasSchedules && indicator) {
			if (indicator.nextElementSibling?.classList.contains("sr-only")) {
				indicator.nextElementSibling.remove();
			}
			indicator.remove();
		}
	}
}

async function initDialogScheduleReport() {
	const openButtons = document.querySelectorAll("[data-dialog='schedule-report']");
	if (!openButtons.length) return;

	let modulePromise;
	for (const openButton of openButtons) {
		const reportId = openButton.dataset.reportId || openButton.dataset.id || null;
		const scheduleId = openButton.dataset.scheduleId || null;
		let controller;
		let opening;

		openButton.addEventListener("click", async () => {
			if (opening) return;
			opening = (async () => {
				modulePromise ??= import(
					/* webpackChunkName: "schedule-report-dialog" */ "~/js/dialogs/schedule-report.js"
				);
				const { createScheduleReportDialog } = await modulePromise;
				controller ??= createScheduleReportDialog({ reportId, scheduleId });
				const result = await controller.open();
				if (result.status === "submitted") syncScheduleIndicators(result.value);
			})();
			try {
				await opening;
			} catch {
				alert(gettext("Impossibile aprire la finestra. Ricarica la pagina e riprova."));
			} finally {
				opening = null;
			}
		});
	}
}

function initLanguageDropdown() {
	const langDropdownBtn = document.querySelector("#language-dropdown");
	const languageDropdownTemplate = document.querySelector("#language-dropdown-template");
	if (!langDropdownBtn || !languageDropdownTemplate) return;

	const langDropdown = new Popover(langDropdownBtn, {
		trigger: "click",
		placement: "bottom-center",
	});
	langDropdown.setContent(languageDropdownTemplate);

	const langDropdownButtons = document.querySelectorAll("button[data-language]");
	if (!langDropdownButtons) return;

	for (const button of langDropdownButtons) {
		button.addEventListener("click", () => {
			const formData = new FormData();
			formData.append("language", button.dataset.language);

			fetch(window._galada.urls.setLanguage, {
				method: "POST",
				headers: {
					"X-CSRFToken": window._galada.csrfToken,
				},
				body: formData,
			}).then((response) => {
				if (response.ok) {
					window.location.reload(response.url);
				}
			});
		});
	}
}

function initSpinner() {
	const spinner = document.querySelector("#spinner-template");
	if (!spinner) return;

	window._galada.spinner = {
		defaultMessage: gettext("Caricamento in corso"),
		element: spinner,
		message: spinner.querySelector("span[data-message]"),
		show: (message = "") => {
			window._galada.spinner.message.textContent = message || window._galada.spinner.defaultMessage;
			spinner.classList.remove("hidden");
		},
		hide: () => {
			spinner.classList.add("hidden");
		},
	};
}

function initSwitchTheme() {
	const switchThemeBtn = document.querySelector("#switch-theme");
	if (!switchThemeBtn) return;

	const switchThemeIcon = (theme) => {
		return `<img src="${
			theme === "light" ? moonSvg : sunSvg
		}" class="pointer-events-none" alt="Switch theme" />`;
	};

	const toggleTheme = async () => {
		const theme = localStorage.getItem("theme");
		const newTheme = theme === "light" ? "dark" : "light";
		window.dispatchEvent(new CustomEvent("themeChange", { detail: { newTheme: newTheme } }));

		document.documentElement.classList.remove(theme);
		document.documentElement.classList.add(newTheme);
		switchThemeBtn.innerHTML = switchThemeIcon(newTheme);
		localStorage.setItem("theme", newTheme);
	};

	const theme = localStorage.getItem("theme");
	if (!theme) {
		const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");
		localStorage.setItem("theme", prefersDarkScheme.matches ? "dark" : "light");
	}
	switchThemeBtn.innerHTML = switchThemeIcon(theme);
	switchThemeBtn.addEventListener("click", async () => {
		if (
			!document.startViewTransition ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			toggleTheme();
			return;
		}

		await document.startViewTransition(() => {
			toggleTheme();
		}).ready;
	});
}

function initUserDropdown() {
	const userDropdownBtn = document.querySelector("#user-dropdown");
	const userDropdownTemplate = document.querySelector("#user-dropdown-template");
	if (userDropdownBtn && userDropdownTemplate) {
		const userDropdown = new Popover(userDropdownBtn, {
			trigger: "click",
			placement: "bottom-end",
		});
		userDropdown.setContent(userDropdownTemplate);
	}
}

document.addEventListener("DOMContentLoaded", async () => {
	initDialogMenu();
	initDialogUserReport();
	initDialogScheduleReport();
	initLanguageDropdown();
	initSwitchTheme();
	initSpinner();
	initUserDropdown();

	window._galada = window._galada || {};
	window._galada.isMobile = isMobile();
	window._galada.isTablet = isTablet();
});
